import { NormalizedNumber, UnixDuration } from '@sparkdotfi/common-universal'

import { SPK_STAKING_POINTS_MULTIPLIER } from '@/config/consts'
import { assets } from '@/ui/assets'
import { Button } from '@/ui/atoms/button/Button'
import { ExternalLink } from '@/ui/atoms/link/Link'
import { Panel } from '@/ui/atoms/panel/Panel'
import { links } from '@/ui/constants/links'
import { Info } from '@/ui/molecules/info/Info'
import { cn } from '@/ui/utils/style'
import { testIds } from '@/ui/utils/testIds'

type StakeSpkActionProps =
  | {
      type: 'connected'
      spkBalance: NormalizedNumber
      stake: () => void
    }
  | {
      type: 'disconnected'
      connectWallet: () => void
      tryInSandbox: () => void
    }

export type StakeSpkCTAPanelProps = {
  epochDuration: UnixDuration
  className?: string
} & StakeSpkActionProps

export function StakeSpkCTAPanel({ className, epochDuration, ...actionsProps }: StakeSpkCTAPanelProps) {
  return (
    <Panel
      spacing="m"
      className={cn(
        'flex flex-col justify-between gap-4',
        'bg-cover bg-right bg-no-repeat',
        'bg-[url(/src/ui/assets/spark-staking/stake-cta-panel-bg.svg)] bg-primary-inverse',
        className,
      )}
    >
      <div className="flex flex-col gap-4">
        <div className="typography-heading-3 sm:typography-heading-2 text-primary-inverse">
          Stake SPK and earn
          <br />
          <span className="inline-flex items-center gap-1.5">
            <img src={assets.points.pointsIcon} className="size-4 rounded-full sm:size-8" />
            <span className="bg-[linear-gradient(165deg,#FA43BD_-6.94%,#FFCD4D_124.69%)] bg-clip-text text-transparent">
              Spark Points
            </span>
          </span>
        </div>
        <div className="typography-body-3 flex flex-col text-tertiary">
          <div>Stake SPK Token and start earning Spark Points</div>
          <div>
            Withdrawal delay {formatWithdrawalDuration(epochDuration)}.{' '}
            <Info className="inline text-secondary">
              The withdrawal delay is part of the underlying staking infrastructure provided by Symbiotic.{' '}
              <ExternalLink to={links.docs.spkStaking}>Learn more</ExternalLink>
            </Info>
          </div>
          <div>
            Staking may involve some risks. Please read more{' '}
            <ExternalLink to={links.docs.stakingRisks}>here</ExternalLink>.
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex items-baseline gap-4">
          <div className="typography-label-2 text-tertiary">Multiplier:</div>
          <div className="typography-heading-2 flex items-baseline gap-[3px] text-primary-inverse">
            <span className="text-white">{SPK_STAKING_POINTS_MULTIPLIER}</span>
            <span className="bg-gradient-spark-primary-2 bg-clip-text text-transparent">x</span>
          </div>
        </div>
        <Actions {...actionsProps} />
      </div>
    </Panel>
  )
}

function Actions(props: StakeSpkActionProps) {
  if (props.type === 'connected') {
    return (
      <Button
        variant="primary"
        size="l"
        className="w-full"
        onClick={props.stake}
        disabled={props.spkBalance.isZero()}
        data-testid={testIds.spkStaking.ctaPanel.stakeButton}
      >
        Stake
      </Button>
    )
  }

  if (props.type === 'disconnected') {
    return (
      <div className="grid grid-cols-2 gap-4">
        <Button variant="primary" size="l" onClick={props.connectWallet}>
          Connect Wallet
        </Button>
        <Button variant="secondary" size="l" onClick={props.tryInSandbox}>
          Try in Sandbox
        </Button>
      </div>
    )
  }
}

function formatWithdrawalDuration(epochDuration: UnixDuration): string {
  const oneEpoch = epochDuration.toSeconds()
  const minWeeks = oneEpoch / UnixDuration({ days: 7 }).toSeconds()
  const minDays = oneEpoch / UnixDuration({ days: 1 }).toSeconds()
  const minHours = oneEpoch / UnixDuration({ hours: 1 }).toSeconds()
  const minMinutes = oneEpoch / UnixDuration({ minutes: 1 }).toSeconds()
  const minSeconds = oneEpoch / UnixDuration({ seconds: 1 }).toSeconds()

  if (minWeeks > 0) {
    return `between ${minWeeks} and ${2n * minWeeks} weeks`
  }
  if (minDays > 0) {
    return `between ${minDays} and ${2n * minDays} days`
  }
  if (minHours > 0) {
    return `between ${minHours} and ${2n * minHours} hours`
  }
  if (minMinutes > 0) {
    return `between ${minMinutes} and ${2n * minMinutes} minutes`
  }

  return `between ${minSeconds} and ${2n * minSeconds} seconds`
}
